// Turns a drop or a file picker into a flat list of files plus the folder tree they came from.

export interface UploadEntry {
  file: File;
  // Parent folder relative to the drop root. An empty string means the file sits at the root.
  path: string;
}

export interface IScanResult {
  entries: UploadEntry[];
  // Every folder found, including empty ones and intermediate parents
  directories: string[];
  truncated: boolean;
}

interface IScanAcc {
  entries: UploadEntry[];
  directories: Set<string>;
  truncated: boolean;
}

export const SCAN_MAX_FILES = 2000;
const MAX_PATH_DEPTH = 32;
const MAX_SEGMENT_LEN = 255;
// Characters ensurePaths() strips server-side; stripping them here too keeps the paths the
// server echoes back identical to the ones the directory map is keyed by.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_SEGMENT_CHARS = /[\\:*?"<>|\u0000-\u001f\u007f]/g;

/**
 * Normalize a folder path the same way the API does, so what we show is what gets stored.
 * macOS emits NFD and Windows NFC; without normalizing, one folder from two machines would end
 * up as two rows. The API re-does all of this: it never trusts these values.
 */
export function cleanPath(raw: string): string {
  return raw
    .split("/")
    .map((segment) =>
      segment.normalize("NFC").replace(FORBIDDEN_SEGMENT_CHARS, "").trim(),
    )
    .filter((segment) => segment && segment !== "." && segment !== "..")
    .slice(0, MAX_PATH_DEPTH)
    .map((segment) => segment.slice(0, MAX_SEGMENT_LEN))
    .join("/");
}

// Register a folder and every folder above it, so a parent always exists for its children
function addWithAncestors(directories: Set<string>, path: string) {
  if (!path) return;
  const segments = path.split("/");
  segments.forEach((_, i) =>
    directories.add(segments.slice(0, i + 1).join("/")),
  );
}

/**
 * Read a directory to the end.
 *
 * readEntries hands back at most ~100 entries per call in Chrome and signals the end with an
 * empty batch, so a single call silently truncates any real folder. It also must not be
 * called again until the previous callback has fired; recursing from inside the callback
 * satisfies both. Firefox returns everything in one go and simply ends a batch earlier.
 */
function readAllEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    const readBatch = () =>
      reader.readEntries((batch) => {
        if (!batch.length) return resolve(all);
        batch.forEach((entry) => all.push(entry));
        readBatch();
      }, reject);
    readBatch();
  });
}

function entryToFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function walkEntry(
  entry: FileSystemEntry,
  parent: string,
  acc: IScanAcc,
  onProgress?: (found: number) => void,
) {
  if (acc.truncated) return;

  if (entry.isFile) {
    if (acc.entries.length >= SCAN_MAX_FILES) {
      acc.truncated = true;
      return;
    }
    // The file's own name is not cleaned and never decides whether the file goes in: the
    // server sanitises the S3 key itself and keeps the original name in file_name.
    const file = await entryToFile(entry as FileSystemFileEntry);
    acc.entries.push({ file, path: parent });
    if (onProgress) onProgress(acc.entries.length);
    return;
  }
  if (!entry.isDirectory) return;

  // Only folder names are cleaned. One that cleans to nothing is a segment the server would
  // drop from the path as well, so its contents move up into the parent instead of vanishing.
  const name = cleanPath(entry.name);
  const path = name ? (parent ? `${parent}/${name}` : name) : parent;
  // Registered before descending, so a folder holding nothing still reaches the API
  if (path) acc.directories.add(path);
  if (path.split("/").length >= MAX_PATH_DEPTH) return;

  const children = await readAllEntries(
    (entry as FileSystemDirectoryEntry).createReader(),
  );
  // Sequential on purpose: a wide tree walked in parallel opens a reader per folder
  for (let i = 0; i < children.length; i++)
    await walkEntry(children[i], path, acc, onProgress);
}

/**
 * Scan a drop. Handles folders, which e.dataTransfer.files cannot see at all.
 *
 * Must be called before the drop handler yields: see the note on the item list below.
 */
export async function scanDataTransfer(
  dataTransfer: DataTransfer,
  onProgress?: (found: number) => void,
): Promise<IScanResult> {
  // The item list is emptied the moment this handler yields, so every entry has to be
  // taken now, synchronously, ahead of the first await.
  const roots: FileSystemEntry[] = [];
  const plainFiles: File[] = [];
  Array.from(dataTransfer.items || []).forEach((item) => {
    if (item.kind !== "file") return;
    const entry =
      typeof item.webkitGetAsEntry === "function"
        ? item.webkitGetAsEntry()
        : null;
    if (entry) {
      roots.push(entry);
      return;
    }
    // No entry API: degrade to flat files rather than dropping the whole thing
    const file = item.getAsFile();
    if (file) plainFiles.push(file);
  });

  if (!roots.length)
    return scanFileList(
      plainFiles.length ? plainFiles : Array.from(dataTransfer.files || []),
    );

  const acc: IScanAcc = {
    entries: [],
    directories: new Set<string>(),
    truncated: false,
  };
  for (let i = 0; i < roots.length; i++)
    await walkEntry(roots[i], "", acc, onProgress);

  acc.entries.forEach(({ path }) => addWithAncestors(acc.directories, path));
  return {
    entries: acc.entries,
    directories: Array.from(acc.directories),
    truncated: acc.truncated,
  };
}

/**
 * Scan an <input> selection. Serves both the plain multiple input (no webkitRelativePath,
 * so everything lands at the root) and the webkitdirectory one.
 *
 * A picked folder cannot carry its empty subfolders: a FileList holds files and nothing else.
 * Only a drop can preserve those.
 */
export function scanFileList(files: FileList | File[]): IScanResult {
  const entries: UploadEntry[] = [];
  const directories = new Set<string>();
  const list = Array.from(files);

  list.forEach((file) => {
    if (entries.length >= SCAN_MAX_FILES) return;
    // The last segment is the file itself. It comes off before the cleaning, so a name that
    // cleans to nothing cannot make the folder above it the segment that gets dropped.
    const segments = (file.webkitRelativePath || "").split("/");
    segments.pop();
    const path = cleanPath(segments.join("/"));
    entries.push({ file, path });
    addWithAncestors(directories, path);
  });

  return {
    entries,
    directories: Array.from(directories),
    truncated: list.length > SCAN_MAX_FILES,
  };
}
